import ConfigDB from "../Config/ThemeConfig";
import { classes } from "../Data/Layouts";
import { updateMyPreferences } from "../api/users";
import { DEFAULT_LAYOUT_SLUG, LAYOUT_SLUGS } from "../constants/layout";

/**
 * Theme/layout preferences shared between the header appearance menu, the
 * MoonLight (dark/light) toggle and app startup.
 *
 * localStorage is the runtime source of truth (so the choice survives reloads),
 * and we additionally best-effort sync it to the signed-in user's account so the
 * same look follows them across devices.
 */
const THEME_KEYS = ["layout", "sidebar_layout", "mix_background_layout", "layout_type"];

const MIX_CLASSES = ["light-only", "dark-only", "dark-sidebar"];
const LAYOUT_TYPE_CLASSES = ["ltr", "rtl", "box-layout"];

export function getStoredThemePreferences() {
  const prefs = {};
  THEME_KEYS.forEach((key) => {
    const value = localStorage.getItem(key);
    if (value) {
      prefs[key] = value;
    }
  });
  return prefs;
}

/** Apply the dark/light/dark-sidebar background mode to the document + storage. */
export function applyMixBackground(value) {
  const mode = MIX_CLASSES.includes(value) ? value : "light-only";
  document.body.classList.remove(...MIX_CLASSES);
  document.body.classList.add(mode);
  ConfigDB.data.color.mix_background_layout = mode;
  localStorage.setItem("mix_background_layout", mode);
}

/** Apply the page direction / box layout to the document + storage. */
export function applyLayoutType(value) {
  const type = LAYOUT_TYPE_CLASSES.includes(value) ? value : "ltr";
  document.body.classList.remove(...LAYOUT_TYPE_CLASSES);
  document.body.classList.add(type);
  document.documentElement.dir = type === "rtl" ? "rtl" : "ltr";
  ConfigDB.data.settings.layout_type = type;
  localStorage.setItem("layout_type", type);
}

/** Re-apply persisted body-level theme classes (run once at app startup). */
export function applyStoredThemeBodyClasses() {
  applyMixBackground(
    localStorage.getItem("mix_background_layout") || ConfigDB.data.color.mix_background_layout || "light-only"
  );
  applyLayoutType(localStorage.getItem("layout_type") || ConfigDB.data.settings.layout_type || "ltr");
}

/** Copy account-saved theme preferences into localStorage (call on login). */
export function hydrateThemeFromUser(user) {
  const theme = user?.preferences?.theme;
  if (!theme || typeof theme !== "object") {
    return;
  }
  THEME_KEYS.forEach((key) => {
    if (theme[key]) {
      localStorage.setItem(key, theme[key]);
    }
  });
}

function cacheThemeOnStoredUser(theme) {
  const raw = localStorage.getItem("credora_user");
  if (!raw) {
    return;
  }
  try {
    const user = JSON.parse(raw);
    user.preferences = { ...(user.preferences || {}), theme };
    localStorage.setItem("credora_user", JSON.stringify(user));
  } catch {
    // ignore malformed cache
  }
}

/**
 * Best-effort: persist the current theme to the signed-in user's account so it
 * follows them to other devices. Silently falls back to localStorage-only when
 * the backend does not (yet) support user preferences.
 */
export async function persistThemePreferences() {
  const theme = getStoredThemePreferences();
  cacheThemeOnStoredUser(theme);
  try {
    await updateMyPreferences({ theme });
  } catch {
    // Choice still persists locally if the account sync fails.
  }
}

export function replacePathLayoutSlug(pathname, newSlug) {
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length && LAYOUT_SLUGS.includes(parts[parts.length - 1])) {
    parts[parts.length - 1] = newSlug;
  } else {
    parts.push(newSlug);
  }
  const pub = process.env.PUBLIC_URL || "";
  return `${pub}/${parts.join("/")}`;
}

/** Switch Cuba layout slug (Dubai, Paris, …) and sync to account. */
export function switchLayoutSlug(slug, addSidebarLayouts) {
  const resolved = LAYOUT_SLUGS.includes(slug) ? slug : DEFAULT_LAYOUT_SLUG;
  const entry = classes.find((item) => Object.keys(item)[0] === resolved);
  if (!entry) {
    return resolved;
  }
  const layoutClass = Object.values(entry)[0];
  localStorage.setItem("layout", resolved);
  localStorage.setItem("sidebar_layout", layoutClass);
  ConfigDB.data.settings.sidebar.type = layoutClass;
  addSidebarLayouts?.(layoutClass);
  void persistThemePreferences();
  return resolved;
}
